import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { LocationResponse } from '../../api/generated';
import type { RootState } from '../index';

// The one copy of the location list. It lives here rather than in useLocations'
// own state because the list has many independent readers — the header
// switcher, the locations page, the settings table, users settings, the
// configuration and treatment plan builder pages, the week overview — and a
// per-hook copy meant a location deleted, renamed or recoloured in settings
// stayed as it was everywhere else until a full page reload.
interface LocationsState {
  items: LocationResponse[];
  isLoading: boolean;
  loadError: string | null;
  // Distinguishes "never fetched" from "fetched, and there are none", so an
  // account with no accessible locations does not refetch on every mount. Set
  // on success only: after a failure the next consumer to mount retries, which
  // is what keeps a transient error from stranding the whole app on an empty
  // list until a reload.
  hasFetched: boolean;
}

const initialState: LocationsState = {
  items: [],
  isLoading: false,
  loadError: null,
  hasFetched: false
};

const locationsSlice = createSlice({
  name: 'locations',
  initialState,
  reducers: {
    // Dispatched before the request goes out. It doubles as the in-flight guard
    // that stops several consumers mounting in the same commit from each firing
    // their own fetch — see useLocations.
    locationsLoadStarted: (state) => {
      state.isLoading = true;
      state.loadError = null;
    },
    locationsLoaded: (state, action: PayloadAction<LocationResponse[]>) => {
      state.items = action.payload;
      state.isLoading = false;
      state.loadError = null;
      state.hasFetched = true;
    },
    locationsLoadFailed: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.loadError = action.payload;
    },
    clearLocations: () => initialState
  }
});

export const { locationsLoadStarted, locationsLoaded, locationsLoadFailed, clearLocations } =
  locationsSlice.actions;

export const selectLocations = (state: RootState): LocationResponse[] => state.locationsSlice.items;

export const selectLocationsIsLoading = (state: RootState): boolean =>
  state.locationsSlice.isLoading;

export const selectLocationsLoadError = (state: RootState): string | null =>
  state.locationsSlice.loadError;

export const selectHasFetchedLocations = (state: RootState): boolean =>
  state.locationsSlice.hasFetched;

export default locationsSlice.reducer;
